import { useEffect, useMemo, useRef, useState } from 'react';

import { scoreTone } from '../lib/format.js';

const MAX_SUGGESTIONS = 8;
const LISTBOX_ID = 'ticker-suggestions';

/**
 * Autocomplete for Indian tickers (``RELIANCE``, ``RELIANCE.NS``).
 *
 * The universe is small enough to filter locally, so suggestions are instant
 * and no request is issued per keystroke.
 *
 * @param {{options: Array<{ticker: string, company_name?: string, sector?: string, score?: number}>,
 *   onSelect: (ticker: string) => void, loading?: boolean}} props
 */
export default function SearchBar({ options = [], onSelect, loading = false }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);

  const matches = useMemo(() => {
    const needle = query.trim().toUpperCase();
    const pool = options.filter((option) => {
      if (!needle) return true;
      const ticker = String(option.ticker ?? '').toUpperCase();
      const company = String(option.company_name ?? '').toUpperCase();
      return ticker.startsWith(needle) || company.includes(needle);
    });
    return pool.slice(0, MAX_SUGGESTIONS);
  }, [options, query]);

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  function commit(option) {
    if (!option) return;
    setQuery(option.ticker);
    setOpen(false);
    onSelect?.(option.ticker);
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit(matches[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const showEmpty = open && matches.length === 0 && !loading;

  return (
    <div ref={containerRef} className="relative w-full">
      <label className="sr-only" htmlFor="ticker-search">
        Search NSE tickers
      </label>
      <input
        id="ticker-search"
        className="input"
        type="text"
        inputMode="search"
        autoComplete="off"
        spellCheck={false}
        placeholder="Search NSE tickers — e.g. RELIANCE, TCS.NS, INFY"
        value={query}
        role="combobox"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={
          open && matches[activeIndex] ? `${LISTBOX_ID}-${matches[activeIndex].ticker}` : undefined
        }
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      />

      {open && (matches.length > 0 || showEmpty) ? (
        <ul
          id={LISTBOX_ID}
          role="listbox"
          className="absolute z-20 mt-2 max-h-80 w-full overflow-y-auto rounded-xl border border-terminal-700 bg-terminal-900/95 py-1 shadow-xl scrollbar-thin"
        >
          {showEmpty ? (
            <li className="px-4 py-3 text-sm text-slate-500">
              {loading ? 'Loading universe…' : 'No matching tickers'}
            </li>
          ) : (
            matches.map((option, index) => {
              const tone = scoreTone(option.score);
              return (
                <li
                  key={option.ticker}
                  id={`${LISTBOX_ID}-${option.ticker}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    commit(option);
                  }}
                  className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-2 text-sm ${
                    index === activeIndex ? 'bg-terminal-700/70' : ''
                  }`}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="font-mono text-slate-100">{option.ticker}.NS</span>
                    <span className="truncate text-xs text-slate-500">
                      {option.company_name ?? option.sector ?? ''}
                    </span>
                  </span>
                  <span className={`font-mono text-xs ${tone.text}`}>
                    {Number.isFinite(option.score) ? option.score : '—'}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
